import { useEffect, useRef, useState } from "react";
import { getWorkflowInProgressCount } from "../services/workflowService";
import { WORKFLOW_COUNT_CHANGED_EVENT } from "../utils/workflowCountEvents";

/**
 * How the badge refreshes (Pooja Pujara on 80430, 2026-10-16 - it used to poll every 60 s):
 * every 5 minutes while the tab is visible; on a route change or when a hidden tab comes back,
 * only if the last read is older than 30 s; and straight after the user's own action, which is
 * when the number actually changes. A hidden tab never polls.
 */
export const WORKFLOW_COUNT_REFRESH_MS = 5 * 60 * 1000;
export const WORKFLOW_COUNT_STALE_MS = 30 * 1000;

/**
 * Epic 80431 - the sidebar "Instances & My Work" badge: in-progress cases in the caller's scope
 * (test runs and finished cases never count - the server leaves them out), from the count-only
 * GET instances/open-count.
 * <p>
 * Deliberately NOT through useApi: a background poll that fails should keep the last count
 * quietly, not raise an alert. Pass `enabled = false` for a user without Instances access - the
 * call would only 403.
 */
const useWorkflowInProgressCount = (enabled: boolean, refreshKey: string) => {
  const [count, setCount] = useState(0);
  // When the last read started; 0 = never. Shared by every trigger so they don't double up.
  const lastReadAt = useRef(0);
  const refreshRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      lastReadAt.current = 0;
      refreshRef.current = () => undefined;
      return undefined;
    }
    let active = true;
    const refresh = () => {
      lastReadAt.current = Date.now();
      getWorkflowInProgressCount()
        .then((total) => {
          if (active) setCount(total);
        })
        .catch(() => {
          // Keep the last count; the next trigger tries again.
        });
    };
    refreshRef.current = refresh;

    const refreshIfVisible = () => {
      if (!document.hidden) refresh();
    };
    const refreshIfStaleAndVisible = () => {
      if (!document.hidden && Date.now() - lastReadAt.current > WORKFLOW_COUNT_STALE_MS) refresh();
    };

    refresh();
    const interval = window.setInterval(refreshIfVisible, WORKFLOW_COUNT_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshIfStaleAndVisible);
    // The user's own start / submit / decision / task / close: the count may just have changed.
    window.addEventListener(WORKFLOW_COUNT_CHANGED_EVENT, refresh);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshIfStaleAndVisible);
      window.removeEventListener(WORKFLOW_COUNT_CHANGED_EVENT, refresh);
    };
  }, [enabled]);

  // A route change refreshes only a stale count - navigating around the app is not a reason to ask.
  useEffect(() => {
    if (!enabled || lastReadAt.current === 0) return;
    if (!document.hidden && Date.now() - lastReadAt.current > WORKFLOW_COUNT_STALE_MS) refreshRef.current();
  }, [enabled, refreshKey]);

  return count;
};

export default useWorkflowInProgressCount;
